import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, SLOT_FUND, WRITE_FLAGS } from "./_shared.js";

/**
 * `INC-RECORD` (@UC0204) — thu NGOÀI ĐỢT (tài trợ, lãi, hoàn phí), T2. QĐ PO 15/09: GIỮ — chuyện thật của ban phụ huynh.
 * Executor = `RecordIncomeService.record` (ledger/record-income.service.ts:96). Không tạo đợt thu, không gán cho thành viên.
 */
export const INC_RECORD = {
  id: "INC-RECORD",
  uc: "@UC0204",
  tier: "T2",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_FUND,
    { id: "soTien", type: "money", required: true, ask: "Thu về bao nhiêu?" },
    { id: "nguon", type: "text", required: true, ask: "Tiền từ đâu? (tên nhà tài trợ / ngân hàng / khoản hoàn)" },
    {
      id: "loai",
      type: "enum",
      required: false,
      ask: "Đây là loại thu nào?",
      choices: ["tài trợ", "lãi", "hoàn phí", "khác"],
      /*
       * 🔴 US-AIV2-0111 C06 (@UC0204) — "ủng hộ"/"hỗ trợ"/"biếu"/"tặng" là TÀI TRỢ. Binding đọc CHÍNH ô này để biết người dùng
       * đã nói rõ đây là tiền ủng hộ (⇒ không hỏi "có phải tiền đóng quỹ của bé X?"). Không vào prompt ⇒ không đổi hash.
       */
      synonyms: { "tài trợ": ["ủng hộ", "hỗ trợ", "biếu", "tặng", "góp thêm"] },
    },
    /**
     * 🔴🔴 US-AIV2-0043 vòng 2 (QĐ PO 22/09) — Ô NGƯỜI TÀI TRỢ, TUỲ CHỌN.
     *
     * Không có ô này thì mọi khoản "tài trợ" qua chat phải đi `sponsorship_external`, vì
     * `sponsorship_member` **bắt buộc `memberId`** (`recordIncomeRequestSchema.superRefine` —
     * *"member tài trợ phải có TÊN, minh bạch"*). Mất mát khi đó **không chỉ là một cái tên**:
     *  · `txType` khác, và nó nằm ở bảng **append-only** ⇒ ghi sai thì phải REVERSAL (§3.3);
     *  · mất `sponsorMemberId` — và đó **đúng bug đã được vá một lần** (US-E7.F1-0724 `@UC0074`:
     *    *"hệ thống hỏi 'ai đóng góp' rồi quên câu trả lời"*) ⇒ đường chat tái tạo nó by construction;
     *  · số liệu vào **sai ô** board/dashboard.
     *
     * ⚠️ TUỲ CHỌN, không bắt buộc: nhà tài trợ NGOÀI nhóm là ca có thật và phổ biến (công ty ABC).
     * Vắng ⇒ `sponsorship_external`; có ⇒ `sponsorship_member` + `memberId`. Và **KHÔNG** suy người
     * tài trợ từ actor (§3.6): actor là O/A/T, người tài trợ thường là member khác.
     */
    { id: "nguoiTaiTro", type: "member", required: false, ask: "Ai trong nhóm tài trợ? (bỏ trống nếu là người/đơn vị ngoài nhóm)" },
  ],
  llm: {
    description: "Ghi một khoản tiền vào quỹ không thuộc đợt thu nào: tài trợ, lãi ngân hàng, hoàn phí.",
    examples: [
      "ghi nhận công ty ABC tài trợ 5 triệu",
      "ghi nhan cong ty abc tai tro 5 trieu",
      "quỹ nhận lãi ngân hàng 120k",
      "quy nhan lai ngan hang 120k",
      "được hoàn 300k tiền đặt cọc, ghi vào quỹ",
      "duoc hoan 300k ghi vao quy",
      "thu ngoài đợt",
      "ghi tài trợ 2tr",
      "phụ huynh bé An ủng hộ thêm 500k",
      "ghi thu tài trợ",
      "nhận tài trợ",
      "cộng 1 triệu vào quỹ từ bán ve chai",
      // ——— 0105: thêm cho đủ 40 ———
      "thu ngoai dot",
      "ghi tai tro 2tr",
      "ghi thu tai tro",
      "nhan tai tro",
      "cong 1 trieu vao quy tu ban ve chai",
      "phu huynh be an ung ho them 500k",
      "ghi khoản ủng hộ 1 triệu vào quỹ",
      "ghi khoan ung ho 1 trieu vao quy",
      "quỹ vừa được cho 3 triệu, ghi vào sổ",
      "quy vua duoc cho 3 trieu ghi vao so",
      "ghi thu lãi tiết kiệm 80k",
      "ghi thu lai tiet kiem 80k",
      "bán đồ cũ được 700k, cho vào quỹ",
      "ban do cu duoc 700k cho vao quy",
      "được hoàn lại tiền thuê xe 400k",
      "duoc hoan lai tien thue xe 400k",
      "một mạnh thường quân ủng hộ 2 triệu",
      "mot manh thuong quan ung ho 2 trieu",
      "ghi thu 250k tiền thừa trả lại quỹ",
      "ghi thu 250k tien thua tra lai quy",
      "cộng thêm tiền vào quỹ không thuộc đợt thu nào",
      "cong them tien vao quy khong thuoc dot thu nao",
      "ghi một khoản thu khác vào quỹ",
      "ghi mot khoan thu khac vao quy",
      "công ty mẹ của một bạn tài trợ 10 triệu",
      "cong ty me cua mot ban tai tro 10 trieu",
      "thu thêm ngoài đợt thu",
      "thu them ngoai dot thu",
      "ghi thu khác",
      // 🔴 US-AIV2-0111 C06 (@UC0204) — chiều ngược của C06: tiền KHÔNG của suất nào (trường hỗ trợ) vẫn là THU NGOÀI. Đo 28/09
      // trên develop: câu này ra menu "chưa rõ ý" 3/3 — nên phải có trong Sổ, không chỉ trong ảnh nghiệm thu.
      "trường hỗ trợ lớp 1 triệu",
      "truong ho tro lop 1 trieu",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["quỹ vừa có biến động gì không"],
  },
  /**
   * 🔴 US-AIV2-0110 S03 (@UC0260 · QĐ PO 4) — ĐÁP ÁN TIỀN của bộ eval, VIẾT TAY từ chính câu (không từ đầu ra model).
   * KHÔNG vào prompt ⇒ không đổi hash theo ca. Câu vắng mặt ở đây: không nói số tiền, hoặc mơ hồ bậc (§4).
   */
  /**
   * 🔴 US-AIV2-0110 S19b (@UC0267) — BẬT lưới đỡ ô tiền: thu ngoài đợt: câu nói một số tiền thì đó là số THU về — không có "mỗi người", không có "đã trả vs còn thiếu".
   * Tiêu chí + lý do ở `aiPageSchema.moneyFromSentence`. Không vào prompt ⇒ hash không đổi.
   */
  moneyFromSentence: "sole-amount",
  evalAmounts: {
    "ghi nhận công ty ABC tài trợ 5 triệu": "5000000",
    "ghi nhan cong ty abc tai tro 5 trieu": "5000000",
    "quỹ nhận lãi ngân hàng 120k": "120000",
    "quy nhan lai ngan hang 120k": "120000",
    "được hoàn 300k tiền đặt cọc, ghi vào quỹ": "300000",
    "duoc hoan 300k ghi vao quy": "300000",
    "ghi tài trợ 2tr": "2000000",
    "phụ huynh bé An ủng hộ thêm 500k": "500000",
    "cộng 1 triệu vào quỹ từ bán ve chai": "1000000",
    "ghi tai tro 2tr": "2000000",
    "cong 1 trieu vao quy tu ban ve chai": "1000000",
    "phu huynh be an ung ho them 500k": "500000",
    "ghi khoản ủng hộ 1 triệu vào quỹ": "1000000",
    "ghi khoan ung ho 1 trieu vao quy": "1000000",
    "quỹ vừa được cho 3 triệu, ghi vào sổ": "3000000",
    "quy vua duoc cho 3 trieu ghi vao so": "3000000",
    "ghi thu lãi tiết kiệm 80k": "80000",
    "ghi thu lai tiet kiem 80k": "80000",
    "bán đồ cũ được 700k, cho vào quỹ": "700000",
    "ban do cu duoc 700k cho vao quy": "700000",
    "được hoàn lại tiền thuê xe 400k": "400000",
    "duoc hoan lai tien thue xe 400k": "400000",
    "một mạnh thường quân ủng hộ 2 triệu": "2000000",
    "mot manh thuong quan ung ho 2 trieu": "2000000",
    "ghi thu 250k tiền thừa trả lại quỹ": "250000",
    "ghi thu 250k tien thua tra lai quy": "250000",
    "công ty mẹ của một bạn tài trợ 10 triệu": "10000000",
    "cong ty me cua mot ban tai tro 10 trieu": "10000000",
    "trường hỗ trợ lớp 1 triệu": "1000000",
    "truong ho tro lop 1 trieu": "1000000",
  },
  executor: "recordIncome.record",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: cửa ghi thu ngoài đợt. */
  endpoints: [
    "POST /funds/{fundId}/income",
  ],
  facts: ["tenQuy", "ghiNhan"],
  templates: {
    /*
     * 🔴 US-AIV2-0108 — `{loai}` ĐÃ GỠ khỏi câu. Ô `loai` là `required: false` và KHÔNG có `autofill`,
     * nên khi người dùng không nói loại thu (*"ghi nhận công ty ABC tài trợ 5 triệu"*) thì
     * `renderTemplate` không có gì để chèn ⇒ ném `TEMPLATE_PLACEHOLDER_MISSING` ⇒ **HTTP 500**.
     * Tìm ra bằng nghiệm thu chạy thật, không bằng test — mọi int-spec của trang đều đi đường đủ ô.
     *
     * Giá trị vẫn hiện ở **thẻ** (T2 in một dòng cho mỗi ô có giá trị, nhãn `labels.loai` = "Loại
     * thu"), nên người dùng KHÔNG mất thông tin — chỉ câu văn xuôi thôi nhắc. `templates.done` vốn
     * đã không dùng `{loai}`, nên đây là làm hai câu nhất quán, không phải cắt bớt.
     */
    confirm: "Ghi thu {soTien} từ “{nguon}” vào quỹ {tenQuy} — {ghiNhan}?",
    done: "Đã ghi thu {soTien} từ “{nguon}” vào quỹ {tenQuy} — {ghiNhan}.",
  },
  labels: {
    soTien: "Số tiền thu",
    nguon: "Nguồn thu",
    // Ô `loai` TUỲ CHỌN; vắng ⇒ engine ghi `other_income` (loại trung tính). Nhãn nói "loại thu" chứ
    // không nói "tài trợ" — nhãn không được khẳng định một thứ người dùng chưa chọn.
    loai: "Loại thu",
    tenQuy: "Quỹ",
    /*
     * 🔴 Phiếu phải NÓI RA khoản này được ghi nhận cho AI — đó là khác biệt giữa "tài trợ của chị
     * Lan" và "tài trợ ngoài nhóm", và nó vào `ledger_transactions` (append-only).
     */
    ghiNhan: "Ghi nhận",
  },
  journey: { outcomes: { done: { next: ["READ-FUND-MONEY"] } } },
  help: { title: "Ghi khoản thu khác", tryNow: "ghi nhận công ty ABC tài trợ 5 triệu" },
} satisfies AiCapabilityPage;
